import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import { useOrganization } from "../OrganizationContext";
import { useUser } from "../UserContext";
import { fetchAccountMapping } from "../api/accountMapping";
import { syncStripeSubscription } from "../api/stripeBilling";
import { normalizeUrl, sanitizeUserTextInput } from "../utils/security";
import { applyEventOwnersToAccountProducts } from "../utils/productEventOwners";
import { deleteAccountsIfEmpty } from "../utils/ga4AccountSync";

function AssetModal({
  items,
  selectedIds,
  toggle,
  assign,
  close,
  renderItem,
  extraTop,
  getItemId,
  emptyMessage = "No items.",
}) {
  const resolveId = (item) => {
    if (typeof getItemId === "function") {
      return getItemId(item);
    }
    return item?.id ?? null;
  };
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          position: "relative",
          backgroundColor: "var(--background-color)",
          color: "var(--text-color)",
          padding: "2rem 1rem 1rem",
          maxHeight: "80%",
          overflowY: "auto",
          overflowX: "hidden",
          width: "90%",
          maxWidth: "400px",
          borderRadius: "var(--border-radius)",
          border: "1px solid var(--primary-color)",
        }}
      >
        <button
          onClick={close}
          style={{
            position: "absolute",
            top: "0.5rem",
            right: "0.5rem",
            background: "transparent",
            border: "none",
            color: "var(--primary-color)",
            fontSize: "1.25rem",
            cursor: "pointer",
          }}
          aria-label="Close"
        >
          &times;
        </button>
        {extraTop}
        {items.length === 0 ? (
          <p>{emptyMessage}</p>
        ) : (
          <ul>
            {items.map((item) => {
              const itemId = resolveId(item);
              if (!itemId) return null;
              return (
                <li key={itemId} style={{ wordBreak: "break-word" }}>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(itemId)}
                      onChange={() => toggle(itemId)}
                    />
                    {renderItem(item)}
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        <div style={{ marginTop: "1rem" }}>
          <button onClick={assign} disabled={selectedIds.length === 0}>
            Assign Selected
          </button>
          <button onClick={close} style={{ marginLeft: "0.5rem" }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductModal({ value, setValue, submit, close, options }) {
  const [useCustom, setUseCustom] = useState(false);

  const handleSelectChange = (e) => {
    const selected = e.target.value;
    if (selected === "__custom__") {
      setUseCustom(true);
      setValue("");
    } else {
      setUseCustom(false);
      setValue(selected);
    }
  };
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          position: "relative",
          backgroundColor: "var(--background-color)",
          color: "var(--text-color)",
          padding: "2rem 1rem 1rem",
          width: "90%",
          maxWidth: "400px",
          borderRadius: "var(--border-radius)",
          border: "1px solid var(--primary-color)",
        }}
      >
        <button
          onClick={close}
          style={{
            position: "absolute",
            top: "0.5rem",
            right: "0.5rem",
            background: "transparent",
            border: "none",
            color: "var(--primary-color)",
            fontSize: "1.25rem",
            cursor: "pointer",
          }}
          aria-label="Close"
        >
          &times;
        </button>
        <select
          value={useCustom ? "__custom__" : value}
          onChange={handleSelectChange}
          style={{ width: "100%", marginBottom: "0.5rem" }}
        >
          <option value="" disabled>
            Select a product
          </option>
          {options.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
          <option value="__custom__">Product is not listed</option>
        </select>
        {useCustom && (
          <input
            type="text"
            placeholder="Product name"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            style={{ width: "100%", marginBottom: "0.5rem" }}
          />
        )}
        <div style={{ marginTop: "1rem" }}>
          <button onClick={submit} disabled={!value.trim()}>
            Add Product
          </button>
          <button onClick={close} style={{ marginLeft: "0.5rem" }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateAccountsModal({
  value,
  setValue,
  profile,
  setProfile,
  timezone,
  setTimezone,
  submit,
  close,
  isSubmitting,
  error,
}) {
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          position: "relative",
          backgroundColor: "var(--background-color)",
          color: "var(--text-color)",
          padding: "2rem 1rem 1rem",
          width: "90%",
          maxWidth: "420px",
          borderRadius: "var(--border-radius)",
          border: "1px solid var(--primary-color)",
        }}
      >
        <button
          onClick={close}
          style={{
            position: "absolute",
            top: "0.5rem",
            right: "0.5rem",
            background: "transparent",
            border: "none",
            color: "var(--primary-color)",
            fontSize: "1.25rem",
            cursor: "pointer",
          }}
          aria-label="Close"
          disabled={isSubmitting}
        >
          &times;
        </button>
        <h3 style={{ marginTop: 0 }}>Create accounts</h3>
        <p style={{ marginTop: "0.5rem", marginBottom: "0.75rem" }}>
          Enter one account name per line. Blank lines will be ignored.
        </p>
        <textarea
          value={value}
          onChange={(event) => setValue(event.target.value)}
          rows={6}
          style={{
            width: "100%",
            resize: "vertical",
            marginBottom: "0.75rem",
          }}
          placeholder={"Account name\nAnother account"}
          disabled={isSubmitting}
        />
        <label style={{ display: "block", marginBottom: "0.75rem" }}>
          Monitoring profile
          <select
            value={profile}
            onChange={(event) => setProfile(event.target.value)}
            disabled={isSubmitting}
            style={{ width: "100%" }}
          >
            <option value="custom_baseline">Custom baseline</option>
            <option value="automotive_asc">Automotive + ASC</option>
          </select>
        </label>
        <label style={{ display: "block", marginBottom: "0.75rem" }}>
          Reporting timezone
          <input
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
            disabled={isSubmitting}
            placeholder="America/New_York"
            style={{ width: "100%" }}
          />
        </label>
        {error && (
          <p
            role="alert"
            style={{
              color: "var(--danger-color, #c0392b)",
              marginBottom: "0.75rem",
            }}
          >
            {error}
          </p>
        )}
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}
        >
          <button onClick={close} disabled={isSubmitting}>
            Cancel
          </button>
          <button onClick={submit} disabled={isSubmitting}>
            {isSubmitting ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Accounts() {
  const { selectedOrgId } = useOrganization();
  const user = useUser();
  const normalizedUserEmail = useMemo(() => {
    if (typeof user?.email !== "string") return null;
    const trimmed = user.email.trim();
    if (!trimmed) return null;
    return trimmed.toLowerCase();
  }, [user?.email]);
  const [accounts, setAccounts] = useState([]);
  const [unassignedWebsites, setUnassignedWebsites] = useState([]);
  const [discoveredGA4Properties, setDiscoveredGA4Properties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgPlan, setOrgPlan] = useState({
    name: "free",
    monthly_scan_limit: 10,
    stripe_subscription_id: null,
    price_per_store_cents: null,
    price_override_cents: null,
    discount_percent: null,
  });
  
  const [normalizedPlanName, setNormalizedPlanName] = useState("free");
  const [addingWebsiteAccount, setAddingWebsiteAccount] = useState(null);
  const [selectedWebsiteIds, setSelectedWebsiteIds] = useState([]);
  const [addingGA4Account, setAddingGA4Account] = useState(null);
  const [selectedGA4Ids, setSelectedGA4Ids] = useState([]);
  const [newWebsiteUrl, setNewWebsiteUrl] = useState("");
  const [addingProductAccount, setAddingProductAccount] = useState(null);
  const [newProductName, setNewProductName] = useState("");
  const [productOptions, setProductOptions] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newAccountNames, setNewAccountNames] = useState("");
  const [newAccountProfile, setNewAccountProfile] = useState("custom_baseline");
  const [newAccountTimezone, setNewAccountTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"
  );
  const [createAccountsError, setCreateAccountsError] = useState("");
  const [isCreatingAccounts, setIsCreatingAccounts] = useState(false);
  const latestOrgIdRef = useRef(selectedOrgId);
  const [subscriptionSyncError, setSubscriptionSyncError] = useState(null);
  const [isSyncingSubscription, setIsSyncingSubscription] = useState(false);

  const syncSubscription = useCallback(async (orgId) => {
    if (!orgId) return;
    setIsSyncingSubscription(true);
    try {
      await syncStripeSubscription(orgId);
      setSubscriptionSyncError(null);
    } catch (error) {
      console.error("Failed to sync Stripe subscription", error);
      const message =
        error?.message ||
        "We couldn't sync your Stripe subscription. Retry to refresh billing totals.";
      setSubscriptionSyncError(message);
    } finally {
      setIsSyncingSubscription(false);
    }
  }, []);

  useEffect(() => {
    setSubscriptionSyncError(null);
    setIsSyncingSubscription(false);
  }, [selectedOrgId]);

  useEffect(() => {
    const fetchProducts = async () => {
      const { data, error } = await supabase
        .from("product_lookup")
        .select("name");
      if (error) {
        console.error("Failed to load product lookup", error);
        return;
      }
      setProductOptions(data.map((p) => p.name));
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    latestOrgIdRef.current = selectedOrgId;
  }, [selectedOrgId]);

  const loadAccounts = async () => {
    const orgId = selectedOrgId;

    if (!orgId) {
      setAccounts([]);
      setUnassignedWebsites([]);
      setDiscoveredGA4Properties([]);
      setOrgPlan({ name: "free" });
      setNormalizedPlanName("free");
      setLoading(false);
      return;
    }
    setLoading(true);
    const {
      accounts: accountData,
      unassignedWebsites: websites,
      organization,
    } = await fetchAccountMapping(orgId);

    if (latestOrgIdRef.current !== orgId) {
      return;
    }

    setAccounts(accountData || []);
    setUnassignedWebsites(websites || []);
    const normalizedPlan = String(
      organization?.plan?.name || "free"
    ).toLowerCase();
    const planDetails = organization?.plan || {
      name: "free",
      monthly_scan_limit: 10,
      stripe_subscription_id: null,
      price_per_store_cents: null,
      price_override_cents: null,
      discount_percent: null,
    };
    setOrgPlan({
      ...planDetails,
      stripe_subscription_id:
        organization?.stripe_subscription_id ?? planDetails.stripe_subscription_id ?? null,
      price_per_store_cents:
        organization?.price_per_store_cents ?? planDetails.price_per_store_cents ?? null,
      price_override_cents:
        organization?.price_override_cents ?? planDetails.price_override_cents ?? null,
      discount_percent:
        organization?.discount_percent ?? planDetails.discount_percent ?? null,
    });
    setNormalizedPlanName(normalizedPlan);
    setLoading(false);

    if (["paid", "enterprise"].includes(normalizedPlan)) {
      syncSubscription(orgId);
    }
  };

  useEffect(() => {
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId]);

  const handleRetrySubscriptionSync = useCallback(() => {
    if (!selectedOrgId) return;
    syncSubscription(selectedOrgId);
  }, [selectedOrgId, syncSubscription]);

  useEffect(() => {
    setSelectedWebsiteIds([]);
    setAddingWebsiteAccount(null);
    setDiscoveredGA4Properties([]);
    setSelectedGA4Ids([]);
    setAddingGA4Account(null);
  }, [selectedOrgId]);

  useEffect(() => {
    setSelectedWebsiteIds((prev) =>
      prev.filter((id) =>
        unassignedWebsites.some((website) => website.id === id)
      )
    );
  }, [unassignedWebsites]);

  useEffect(() => {
    setSelectedGA4Ids((prev) =>
      prev.filter((id) =>
        discoveredGA4Properties.some((prop) => prop.property_id === id)
      )
    );
  }, [discoveredGA4Properties]);

  const openCreateAccountsModal = () => {
    if (!selectedOrgId) return;
    setNewAccountNames("");
    setNewAccountProfile("custom_baseline");
    setCreateAccountsError("");
    setIsCreateModalOpen(true);
  };

  const closeCreateAccountsModal = () => {
    if (isCreatingAccounts) return;
    setIsCreateModalOpen(false);
    setCreateAccountsError("");
  };

  const handleCreateAccounts = async () => {
    if (!selectedOrgId) return;

    const rawNames = newAccountNames
      .split(/\r?\n/)
      .map((name) => name.trim())
      .filter(Boolean);

    if (rawNames.length === 0) {
      setCreateAccountsError("Enter at least one account name.");
      return;
    }

    const sanitizedRows = [];
    for (const name of rawNames) {
      const { sanitized, error: validationError } = sanitizeUserTextInput(name, {
        maxLength: 120,
      });

      if (!sanitized) {
        setCreateAccountsError(validationError || "Invalid account name.");
        return;
      }

      sanitizedRows.push({
        name: sanitized,
        organization_id: selectedOrgId,
        monitoring_profile: newAccountProfile,
        monitoring_timezone: newAccountTimezone || "America/New_York",
      });
    }

    setIsCreatingAccounts(true);
    setCreateAccountsError("");
    const { error } = await supabase.from("accounts").insert(sanitizedRows);
    setIsCreatingAccounts(false);

    if (error) {
      console.error("Failed to create accounts", error);
      setCreateAccountsError(error.message || "Failed to create accounts.");
      return;
    }

    setIsCreateModalOpen(false);
    setNewAccountNames("");
    await syncSubscription(selectedOrgId);
    await loadAccounts();
  };

  const startAddWebsite = (accountId) => {
    setAddingWebsiteAccount(accountId);
    setSelectedWebsiteIds([]);
    setNewWebsiteUrl("");
  };

  const startAddGA4 = (accountId) => {
    setAddingGA4Account(accountId);
    setSelectedGA4Ids([]);
  };

  const startAddProduct = (accountId) => {
    setAddingProductAccount(accountId);
    setNewProductName("");
  };

  const toggleWebsite = (id) => {
    setSelectedWebsiteIds((prev) =>
      prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id]
    );
  };

  const toggleGA4 = (id) => {
    setSelectedGA4Ids((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
  };

  const handleAddWebsiteUrl = async () => {
    if (!newWebsiteUrl || !addingWebsiteAccount) return;
    const normalized = normalizeUrl(newWebsiteUrl);
    if (!normalized) {
      alert("Enter a valid website URL (http or https).");
      return;
    }

    const { error } = await supabase.from("website_urls").insert({
      url: normalized,
      organization_id: selectedOrgId,
      account_id: addingWebsiteAccount,
    });
    if (error) {
      console.error("Failed to add website", error);
      alert("Failed to add website");
      return;
    }
    setNewWebsiteUrl("");
    await loadAccounts();
  };

  const handleAddProduct = async () => {
    if (!newProductName || !addingProductAccount) return;

    const { sanitized, error: validationError } = sanitizeUserTextInput(
      newProductName,
      { maxLength: 120 },
    );

    if (!sanitized) {
      alert(validationError || "Invalid product name.");
      return;
    }

    const { error } = await supabase.from("products").insert({
      product: sanitized,
      organization_id: selectedOrgId,
      account_id: addingProductAccount,
    });
    if (error) {
      console.error("Failed to add product", error);
      alert("Failed to add product");
      return;
    }
    setAddingProductAccount(null);
    setNewProductName("");
    await loadAccounts();
  };

  const handleAssignWebsites = async () => {
    if (!addingWebsiteAccount || selectedWebsiteIds.length === 0) {
      setAddingWebsiteAccount(null);
      return;
    }
    const { error } = await supabase
      .from("website_urls")
      .update({ account_id: addingWebsiteAccount })
      .in("id", selectedWebsiteIds);
    if (error) {
      console.error("Failed to assign websites", error);
      alert("Failed to assign websites");
    }
    setAddingWebsiteAccount(null);
    loadAccounts();
  };

  const handleAssignGA4 = async () => {
    if (!addingGA4Account || selectedGA4Ids.length === 0) {
      setAddingGA4Account(null);
      return;
    }
    const selectedProps = discoveredGA4Properties.filter((prop) =>
      selectedGA4Ids.includes(prop.property_id)
    );
    if (selectedProps.length === 0) {
      alert("Select at least one GA4 property to assign.");
      return;
    }
    const inserts = selectedProps.map((prop) => {
      const baseRecord = {
        property_id: prop.property_id,
        property_name: prop.property_name,
        organization_id: selectedOrgId,
        account_id: addingGA4Account,
      };
      if (normalizedUserEmail) {
        baseRecord.google_account_email = normalizedUserEmail;
      }
      return baseRecord;
    });

    const {
      data: insertedRows,
      error,
    } = await supabase
      .from("ga4_properties")
      .insert(inserts)
      .select("id, account_id, organization_id");
    if (error) {
      console.error("Failed to assign GA4 properties", error);
      alert("Failed to assign GA4 properties");
      return;
    }

    const insertedIds = (insertedRows || [])
      .map((row) => row.id)
      .filter(Boolean);

    if (insertedIds.length > 0) {
      const { data: eventAuditRows, error: eventAuditError } = await supabase
        .from("ga4_event_audit_results")
        .select("ga4_property_id, event_owner")
        .in("ga4_property_id", insertedIds);

      if (eventAuditError) {
        console.error(
          "Failed to load event audit results for assigned GA4 properties",
          eventAuditError,
        );
      } else if (Array.isArray(eventAuditRows) && eventAuditRows.length > 0) {
        const rowsByProperty = new Map();
        for (const row of eventAuditRows) {
          if (!row || typeof row !== "object") continue;
          const propertyId = row.ga4_property_id;
          if (!propertyId) continue;
          if (!rowsByProperty.has(propertyId)) {
            rowsByProperty.set(propertyId, []);
          }
          rowsByProperty.get(propertyId).push(row);
        }

        for (const inserted of insertedRows || []) {
          const rows = rowsByProperty.get(inserted.id) || [];
          if (!rows.length) continue;
          await applyEventOwnersToAccountProducts({
            accountId: inserted.account_id,
            organizationId: inserted.organization_id,
            ownerRows: rows,
          });
        }
      }
    }

    setDiscoveredGA4Properties((prev) =>
      prev.filter((prop) => !selectedGA4Ids.includes(prop.property_id))
    );
    setSelectedGA4Ids([]);
    setAddingGA4Account(null);
    loadAccounts();
  };

  const checkGA4Access = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      console.log("[GA4] Retrieved session", {
        hasSession: Boolean(sessionData?.session),
        expiresAt: sessionData?.session?.expires_at || null,
        hasProviderToken: Boolean(sessionData?.session?.provider_token),
      });
      let token = sessionData?.session?.provider_token || null;
      if (
        !token ||
        (sessionData.session?.expires_at &&
          sessionData.session.expires_at * 1000 < Date.now())
      ) {
        console.log("[GA4] Refreshing Supabase session for GA4 access");
        const { data: refreshed } = await supabase.auth.refreshSession();
        console.log("[GA4] Session refresh result", {
          hasSession: Boolean(refreshed?.session),
          expiresAt: refreshed?.session?.expires_at || null,
          hasProviderToken: Boolean(refreshed?.session?.provider_token),
        });
        token = refreshed?.session?.provider_token || null;
      }
      console.log("[GA4] Access token state", { tokenAvailable: Boolean(token) });
      if (!token) {
        alert("Missing GA4 access token");
        return;
      }
      const response = await supabase.functions.invoke("ga4-proxy", {
        body: { type: "list-properties" },
        headers: { "x-ga4-authorization": `Bearer ${token}` },
      });
      if (!response) {
        console.error("[GA4] No response when fetching GA4 properties");
        alert("Failed to fetch GA4 properties");
        return;
      }
      const { data, error } = response;
      const sanitizedResponse = {
        accountSummaries: data?.accountSummaries?.map((acc) => ({
          displayName: acc.displayName || acc.name || "",
          propertyCount: acc.propertySummaries?.length || 0,
          propertyNames: acc.propertySummaries?.map((ps) => ps.displayName) || [],
        })),
      };
      console.log("[GA4] ga4-proxy response", sanitizedResponse);
      if (error) {
        console.error("Failed to fetch GA4 properties", error);
        alert("Failed to fetch GA4 properties");
        return;
      }
      const list = [];
      data?.accountSummaries?.forEach((acc) => {
        acc.propertySummaries?.forEach((ps) => {
          list.push({ property: ps.property, displayName: ps.displayName });
        });
      });
      console.log("[GA4] Total GA4 properties discovered", list.length);
      const existing = new Set([
        ...accounts.flatMap((a) =>
          (a.ga4_properties || []).map((p) => p.property_id)
        ),
        ...discoveredGA4Properties.map((p) => p.property_id),
      ]);
      const newProps = list.filter((p) => !existing.has(p.property));
      if (newProps.length === 0) {
        console.log("[GA4] No new GA4 properties detected");
        alert("No new GA4 properties found");
        return;
      }
      setDiscoveredGA4Properties((prev) => {
        const map = new Map(
          (prev || []).map((prop) => [prop.property_id, prop])
        );
        newProps.forEach((p) => {
          map.set(p.property, {
            property_id: p.property,
            property_name: p.displayName,
          });
        });
        const merged = Array.from(map.values());
        merged.sort((a, b) => {
          const aLabel = (a.property_name || a.property_id || "").toLowerCase();
          const bLabel = (b.property_name || b.property_id || "").toLowerCase();
          return aLabel.localeCompare(bLabel);
        });
        return merged;
      });
      alert(
        `${newProps.length} new GA4 propert${
          newProps.length === 1 ? "y" : "ies"
        } ready to assign.`
      );
    } catch (e) {
      console.error("Failed to check GA4 access", e);
    }
  };

  const handleRemoveWebsite = async (id) => {
    if (!window.confirm("Remove this website URL?")) return;
    await supabase.from("website_urls").delete().eq("id", id);
    loadAccounts();
  };

  const handleRemoveGA4 = async (id, accountId) => {
    if (!window.confirm("Remove this GA4 property?")) return;
    const { error } = await supabase
      .from("ga4_properties")
      .delete()
      .eq("id", id);
    if (error) {
      console.error("Failed to delete GA4 property", error);
      return;
    }

    if (accountId) {
      const deletedAccountIds = await deleteAccountsIfEmpty([accountId], supabase);
      if (deletedAccountIds.length > 0) {
        await syncSubscription(selectedOrgId);
      }
    }

    await loadAccounts();
  };

  const handleRemoveProduct = async (id) => {
    if (!window.confirm("Remove this product?")) return;
    await supabase.from("products").delete().eq("id", id);
    loadAccounts();
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm("Delete this account and its mappings?")) return;
    const { error } = await supabase.from("accounts").delete().eq("id", id);
    if (error) {
      console.error("Failed to delete account", error);
      setSubscriptionSyncError(error.message || "Failed to delete dealership.");
      return;
    }
    await syncSubscription(selectedOrgId);
    await loadAccounts();
  };

  const handleEditAccount = async (id, currentName) => {
    const name = prompt("Edit account name:", currentName);
    if (!name) return;

    const { sanitized, error: validationError } = sanitizeUserTextInput(name, {
      maxLength: 120,
    });

    if (!sanitized) {
      alert(validationError || "Invalid account name.");
      return;
    }

    if (sanitized === currentName) return;

    const { error } = await supabase
      .from("accounts")
      .update({ name: sanitized })
      .eq("id", id);
    if (error) {
      console.error("Failed to update account name", error);
      alert("Failed to update account name");
      return;
    }
    loadAccounts();
  };

  return (
    <div style={{ padding: "2rem" }}>
      {isCreateModalOpen && (
        <CreateAccountsModal
          value={newAccountNames}
          profile={newAccountProfile}
          setProfile={setNewAccountProfile}
          timezone={newAccountTimezone}
          setTimezone={setNewAccountTimezone}
          setValue={(value) => {
            setCreateAccountsError("");
            setNewAccountNames(value);
          }}
          submit={handleCreateAccounts}
          close={closeCreateAccountsModal}
          isSubmitting={isCreatingAccounts}
          error={createAccountsError}
        />
      )}
      <h2>Accounts</h2>
      {subscriptionSyncError && selectedOrgId && (
        <div
          role="alert"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            flexWrap: "wrap",
            marginBottom: "1rem",
            padding: "0.75rem 1rem",
            borderRadius: "6px",
            border: "1px solid #d97706",
            backgroundColor: "#fff7ed",
            color: "#92400e",
          }}
        >
          <span>{subscriptionSyncError}</span>
          <button
            onClick={handleRetrySubscriptionSync}
            disabled={isSyncingSubscription}
            style={{
              padding: "0.35rem 0.75rem",
              borderRadius: "4px",
              border: "1px solid #d97706",
              backgroundColor: isSyncingSubscription ? "#fde68a" : "#fbbf24",
              color: "#78350f",
              cursor: isSyncingSubscription ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
          >
            {isSyncingSubscription ? "Retrying…" : "Retry sync"}
          </button>
        </div>
      )}
      <div
        style={{
          marginBottom: "1rem",
          display: "flex",
          gap: "1rem",
          alignItems: "center",
        }}
      >
        <button onClick={openCreateAccountsModal} disabled={!selectedOrgId}>
          Create Accounts
        </button>
        {normalizedPlanName === "admin" ? (
          <span>Admin organization - unlimited access</span>
        ) : normalizedPlanName === "paid" ? (
          <span>Paid organization</span>
        ) : (
          selectedOrgId &&
          !orgPlan.stripe_subscription_id && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                flexWrap: "wrap",
              }}
            >
              <span>Select a paid plan before subscribing.</span>
              <a
                href={`/organizations/${selectedOrgId}/plan`}
                style={{
                  padding: "0.5rem 1rem",
                  backgroundColor: "#0070f3",
                  color: "#fff",
                  borderRadius: "4px",
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Manage plan
              </a>
            </div>
          )
        )}
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {accounts.length === 0 ? (
            <p>No accounts found.</p>
          ) : (
            accounts.map((account) => (
              <div
                key={account.id}
                style={{
                  border: "1px solid #ccc",
                  marginBottom: "1rem",
                  padding: "1rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h3 style={{ margin: 0 }}>{account.name}</h3>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <a href={`/account-audits/${account.id}`}>Audit</a>
                    <button
                      onClick={() => handleEditAccount(account.id, account.name)}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteAccount(account.id)}
                      style={{ marginLeft: "0.5rem" }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}>
                  <div style={{ flex: 1 }}>
                    <strong>Websites</strong>
                    <ul>
                      {(account.website_urls || []).map((w) => (
                        <li key={w.id} style={{ wordBreak: "break-word" }}>
                          {w.url}{" "}
                          <button onClick={() => handleRemoveWebsite(w.id)}>
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                    <button onClick={() => startAddWebsite(account.id)}>
                      Add Website
                    </button>
                  </div>
                  <div style={{ flex: 1 }}>
                    <strong>Products</strong>
                    <ul>
                      {(account.products || []).map((p) => {
                        const ownerLabel =
                          typeof p?.event_owner === "string" && p.event_owner.trim()
                            ? ` — ${p.event_owner.trim()}`
                            : "";
                        return (
                          <li key={p.id} style={{ wordBreak: "break-word" }}>
                            <span>
                              {p.product}
                              {ownerLabel}
                            </span>{" "}
                            <button onClick={() => handleRemoveProduct(p.id)}>
                              Remove
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    <button onClick={() => startAddProduct(account.id)}>
                      Add Product
                    </button>
                  </div>
                  <div style={{ flex: 1 }}>
                    <strong>GA4 Properties</strong>
                    <ul>
                      {(account.ga4_properties || []).map((g) => (
                        <li key={g.id} style={{ wordBreak: "break-word" }}>
                          {g.property_name ? `${g.property_name} (${g.property_id})` : g.property_id}{" "}
                          <button onClick={() => handleRemoveGA4(g.id, account.id)}>
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                    {discoveredGA4Properties.length > 0 && (
                      <p
                        style={{
                          fontSize: "0.85rem",
                          color: "var(--text-muted-color, #475569)",
                          marginTop: 0,
                          marginBottom: "0.5rem",
                        }}
                      >
                        {`${discoveredGA4Properties.length} new GA4 propert${
                          discoveredGA4Properties.length === 1 ? "y" : "ies"
                        } ready to assign.`}
                      </p>
                    )}
                    <button onClick={() => startAddGA4(account.id)}>
                      Add GA4 Property
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
          <div style={{ marginTop: "2rem" }}>
            <h3>Unassigned Websites</h3>
            <div style={{ margin: "0.5rem 0" }}>
              <a href="/ga4-audit" data-testid="ga4-audit-link">
                Check GA4 Access
              </a>
            </div>
            <table className="ui-table"
              style={{ width: "100%", borderCollapse: "collapse" }}
              data-testid="unassigned-table"
            >
              <thead>
                <tr>
                  <th style={{ border: "1px solid #ccc" }}>Website URL</th>
                </tr>
              </thead>
              <tbody>
                {unassignedWebsites.length === 0 ? (
                  <tr>
                    <td
                      style={{ border: "1px solid #ccc", textAlign: "center" }}
                    >
                      None
                    </td>
                  </tr>
                ) : (
                  unassignedWebsites.map((website) => (
                    <tr key={website.id}>
                      <td style={{ border: "1px solid #ccc" }}>
                        {website.url}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {addingWebsiteAccount && (
            <AssetModal
              items={unassignedWebsites}
              selectedIds={selectedWebsiteIds}
              toggle={toggleWebsite}
              assign={handleAssignWebsites}
              close={() => setAddingWebsiteAccount(null)}
              renderItem={(w) => w.url}
              extraTop={
                <div style={{ marginBottom: "0.5rem" }}>
                  <input
                    type="text"
                    placeholder="https://example.com"
                    value={newWebsiteUrl}
                    onChange={(e) => setNewWebsiteUrl(e.target.value)}
                    style={{ width: "100%", marginBottom: "0.5rem" }}
                  />
                  <button onClick={handleAddWebsiteUrl}>Add Website</button>
                </div>
              }
            />
          )}
          {addingGA4Account && (
            <AssetModal
              items={discoveredGA4Properties}
              selectedIds={selectedGA4Ids}
              toggle={toggleGA4}
              assign={handleAssignGA4}
              close={() => setAddingGA4Account(null)}
              renderItem={(p) =>
                p.property_name
                  ? `${p.property_name} (${p.property_id})`
                  : p.property_id
              }
              getItemId={(p) => p.property_id}
              emptyMessage="No discovered GA4 properties. Use “Check GA4 Access” to find new properties."
              extraTop={
                <div style={{ marginBottom: "0.5rem" }}>
                  <button onClick={checkGA4Access} data-testid="popup-ga4-audit">
                    Check GA4 Access
                  </button>
                </div>
              }
            />
          )}
          {addingProductAccount && (
            <ProductModal
              value={newProductName}
              setValue={setNewProductName}
              submit={handleAddProduct}
              close={() => {
                setAddingProductAccount(null);
                setNewProductName("");
              }}
              options={productOptions}
            />
          )}
        </>
      )}
    </div>
  );
}

